"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";

import type { ToastAction } from "@/lib/toast";

type SystemToast = { message: string; kind: "error" | "success"; actions?: ToastAction[] };
type NotificationToast = { id: string; title: string; body: string; url: string };

type ToastItem =
  | { key: string; type: "system"; message: string; kind: "error" | "success"; actions?: ToastAction[] }
  | { key: string; type: "notification"; title: string; body: string; url: string };

const SYSTEM_TTL = 4000;
const NOTIFICATION_TTL = 6000;

export function GlobalToast() {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, number>());

  const dismiss = useCallback((key: string) => {
    const timer = timers.current.get(key);
    if (timer !== undefined) window.clearTimeout(timer);
    timers.current.delete(key);
    setItems((current) => current.filter((item) => item.key !== key));
  }, []);

  useEffect(() => {
    function schedule(key: string, ttl: number) {
      const existing = timers.current.get(key);
      if (existing !== undefined) window.clearTimeout(existing);
      const timer = window.setTimeout(() => dismiss(key), ttl);
      timers.current.set(key, timer);
    }

    function onToast(event: Event) {
      const detail = (event as CustomEvent<SystemToast>).detail;
      if (!detail?.message) return;
      const key = `system:${Date.now()}:${Math.random().toString(36).slice(2, 6)}`;
      const actions = Array.isArray(detail.actions) && detail.actions.length ? detail.actions : undefined;
      setItems((current) => [...current.slice(-3), { key, type: "system", message: detail.message, kind: detail.kind ?? "success", actions }]);
      // Messages that need a decision stay until the user answers.
      if (!actions) schedule(key, SYSTEM_TTL);
    }

    function onNotificationToast(event: Event) {
      const detail = (event as CustomEvent<NotificationToast>).detail;
      if (!detail?.title || !detail.id) return;
      const key = `notification:${detail.id}`;
      setItems((current) => [...current.filter((item) => item.key !== key).slice(-3), { key, type: "notification", title: detail.title, body: detail.body ?? "", url: detail.url ?? "/app/all/home" }]);
      schedule(key, NOTIFICATION_TTL);
    }

    window.addEventListener("tairoh-toast", onToast);
    window.addEventListener("tairoh-notification-toast", onNotificationToast);
    const activeTimers = timers.current;
    return () => {
      window.removeEventListener("tairoh-toast", onToast);
      window.removeEventListener("tairoh-notification-toast", onNotificationToast);
      for (const timer of activeTimers.values()) window.clearTimeout(timer);
      activeTimers.clear();
    };
  }, [dismiss]);

  if (!items.length) return null;
  return (
    <div data-testid="global-toast" className="pointer-events-none fixed right-3 top-16 z-[9999] grid w-[min(20rem,calc(100vw-1.5rem))] gap-2">
      {items.map((item) => item.type === "system" ? (
        <div
          key={item.key}
          role={item.kind === "error" ? "alert" : "status"}
          aria-live="polite"
          data-testid="global-toast-system"
          className={`pointer-events-auto rounded-md border px-3 py-2 text-sm shadow-lg ${item.kind === "error" ? "border-red-200 bg-red-50 text-[color:var(--danger)]" : "border-[color:var(--line)] bg-[color:var(--bg-subtle)] text-[color:var(--fg)]"}`}
        >
          <span className="mb-0.5 block text-[10px] font-semibold tracking-wide text-[color:var(--muted)]">システム</span>
          {item.message}
          {item.actions ? (
            <span className="mt-2 flex justify-end gap-2">
              {item.actions.map((action, index) => (
                <button
                  key={`${item.key}-${index}-${action.label}`}
                  type="button"
                  data-testid={`global-toast-action-${index}`}
                  className={action.primary ? "button-primary h-7 rounded-md px-2 text-xs font-semibold" : "button-secondary h-7 rounded-md px-2 text-xs"}
                  onClick={() => { dismiss(item.key); action.onClick(); }}
                >
                  {action.label}
                </button>
              ))}
            </span>
          ) : null}
        </div>
      ) : (
        <Link
          key={item.key}
          href={item.url}
          data-testid="global-toast-notification"
          className="pointer-events-auto grid gap-0.5 rounded-md border border-[color:var(--line)] border-l-4 border-l-[color:var(--accent-strong)] bg-white px-3 py-2 text-left text-sm shadow-lg"
          onClick={() => dismiss(item.key)}
        >
          <span className="text-[10px] font-semibold tracking-wide text-[color:var(--accent-strong)]">通知</span>
          <span className="min-w-0 truncate font-medium">{item.title}</span>
          {item.body ? <span className="line-clamp-2 text-xs text-[color:var(--muted)]">{item.body}</span> : null}
        </Link>
      ))}
    </div>
  );
}
